import { useFormik } from "formik";
import * as Yup from "yup";
import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import {
  getDailyReportUsersService,
  getDailyReportService,
  getDailyReportBoxesService,
} from "modules/came/services/informeService";

export const useFormInforme = (onSubmit, initialData = {}) => {
  // ========== ESTADOS PRINCIPALES ==========
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);

  // Estados para usuarios y cajas
  const [users, setUsers] = useState([
    { id: "TODOS", name: "TODOS", label: "TODOS", value: "TODOS" },
  ]);
  const [boxes, setBoxes] = useState([
    { id: "TODAS", name: "TODAS", label: "TODAS", value: "TODAS" },
  ]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingBoxes, setLoadingBoxes] = useState(false);
  const [errorUsers, setErrorUsers] = useState(null);
  const [errorBoxes, setErrorBoxes] = useState(null);
  const [lastErrorType, setLastErrorType] = useState(null);

  // Refs para control de montaje y carga única
  const isMounted = useRef(true);
  const hasLoadedDataRef = useRef(false);

  // ========== FUNCIONES DE UTILIDAD ==========

  const classifyError = useCallback((error) => {
    const errorMessage = error?.message || error?.toString() || "";

    if (
      errorMessage.includes("No hay token") ||
      errorMessage.includes("No autorizado") ||
      errorMessage.includes("sesión") ||
      errorMessage.includes("401")
    ) {
      return { type: "auth", severity: "error", persist: true };
    }

    if (
      errorMessage.includes("conexión") ||
      errorMessage.includes("servidor") ||
      errorMessage.includes("timeout") ||
      errorMessage.includes("Network Error")
    ) {
      return { type: "network", severity: "warning", persist: true };
    }

    if (
      errorMessage.includes("formato") ||
      errorMessage.includes("inválido") ||
      errorMessage.includes("validación") ||
      errorMessage.includes("400")
    ) {
      return { type: "validation", severity: "warning", persist: false };
    }

    if (
      errorMessage.includes("no encontrado") ||
      errorMessage.includes("no existe") ||
      errorMessage.includes("404")
    ) {
      return { type: "notFound", severity: "warning", persist: false };
    }

    return { type: "general", severity: "error", persist: false };
  }, []);

  const extractErrorMessage = useCallback((error) => {
    if (typeof error === "string") return error;

    if (error.response) {
      const { data, status, statusText } = error.response;

      if (data?.message) return data.message;
      if (data?.error) return data.error;
      if (data?.msm) return data.msm;
      if (data?.response?.message) return data.response.message;
      if (data?.response?.error) return data.response.error;

      const statusMessages = {
        400: "Solicitud incorrecta. Verifique los datos enviados.",
        401: "No autorizado. Su sesión ha expirado.",
        403: "No tiene permisos para realizar esta acción.",
        404: "Recurso no encontrado.",
        409: "Conflicto: El recurso ya existe.",
        422: "Error de validación en los datos.",
        500: "Error interno del servidor.",
        502: "Error de puerta de enlace.",
        503: "Servicio no disponible temporalmente.",
        504: "Tiempo de espera agotado.",
      };

      return statusMessages[status] || `Error ${status}: ${statusText}`;
    }

    if (error.request) {
      return "No se pudo conectar con el servidor. Verifique su conexión a internet.";
    }

    return error.message || "Error desconocido al procesar la solicitud";
  }, []);

  // ========== EFECTOS DE CICLO DE VIDA ==========

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // ========== SERVICIO DE CARGA DE DATOS ==========

  const loadUsersAndBoxes = useCallback(async () => {
    if (hasLoadedDataRef.current) return;
    if (loadingUsers || loadingBoxes) return;

    try {
      setLoadingUsers(true);
      setLoadingBoxes(true);
      setErrorUsers(null);
      setErrorBoxes(null);

      const [usersResult, boxesResult] = await Promise.allSettled([
        getDailyReportUsersService(),
        getDailyReportBoxesService(),
      ]);

      if (!isMounted.current) return;

      if (usersResult.status === "fulfilled" && usersResult.value?.success) {
        const usersData = usersResult.value.data;
        if (Array.isArray(usersData) && usersData.length > 0) {
          setUsers(usersData);
        }
      } else {
        setUsers([{ id: "TODOS", name: "TODOS", label: "TODOS", value: "TODOS" }]);
      }

      if (boxesResult.status === "fulfilled" && boxesResult.value?.success) {
        const boxesData = boxesResult.value.data;
        if (Array.isArray(boxesData) && boxesData.length > 0) {
          setBoxes(boxesData);
        }
      } else {
        setBoxes([{ id: "TODAS", name: "TODAS", label: "TODAS", value: "TODAS" }]);
      }

      hasLoadedDataRef.current = true;
    } catch (error) {
      console.error("Error loading users and boxes:", error);
      if (isMounted.current) {
        const errorMsg = extractErrorMessage(error);
        setErrorUsers(`Error al cargar datos: ${errorMsg}`);
        setErrorBoxes(`Error al cargar datos: ${errorMsg}`);
      }
    } finally {
      if (isMounted.current) {
        setLoadingUsers(false);
        setLoadingBoxes(false);
      }
    }
  }, [loadingUsers, loadingBoxes, extractErrorMessage]);

  useEffect(() => {
    if (!hasLoadedDataRef.current && isMounted.current) {
      loadUsersAndBoxes();
    }
  }, [loadUsersAndBoxes]);

  // ========== FUNCIONES CORRECTAS PARA CÁLCULO DE IVA (19%) ==========

  /**
   * Obtiene el valor NETO (sin IVA) a partir de un valor que ya incluye IVA
   * Fórmula: Valor Neto = Valor con IVA ÷ 1.19
   */
  const obtenerValorNetoSinIVA = (valorConIVA) => {
    if (!valorConIVA || valorConIVA === 0) return 0;
    return Math.round(valorConIVA / 1.19);
  };

  /**
   * Obtiene el valor del IVA (19%) a partir de un valor que ya incluye IVA
   * Fórmula: IVA = Valor con IVA - Valor Neto
   */
  const obtenerIVA = (valorConIVA) => {
    if (!valorConIVA || valorConIVA === 0) return 0;
    const valorNeto = valorConIVA / 1.19;
    return Math.round(valorConIVA - valorNeto);
  };

  // ========== FUNCIÓN PARA PROCESAR DATOS DEL REPORTE ==========

  const processReportData = useCallback((rawData) => {
    if (!rawData) return null;

    const totalOperationsEfe = rawData.totalOperationsEfe || 0;
    const totalOperationsTar = rawData.totalOperationsTar || 0;
    const totalOperationsEfeImp = rawData.totalOperationsEfeImp || 0;
    const totalOperationsTarImp = rawData.totalOperationsTarImp || 0;

    const countOperationsEfe = rawData.countOperationsEfe || 0;
    const countOperationsTar = rawData.countOperationsTar || 0;
    const miselaneos = rawData.miselaneos || 0;
    const recargasMensuales = rawData.recargasMensuales || 0;
    const countCobrosCancelados = rawData.countCobrosCancelados || 0;
    const turn = rawData.turn || 0;

    const perdidosTotalBruto = rawData.ticketesPerdidos || 0;
    const perdidosCantidad = rawData.countTicketPerdidos || 0;
    const ivaPerdidosBackend = rawData.ticketesPerdidosSinIva || 0;
    const perdidosTotalSinIva = rawData.ticketesPerdidosSinIva || 0;

    // ========== VALORES BRUTOS (con impuestos incluidos) ==========
    const recaudoTarifasBruto = totalOperationsEfe + totalOperationsTar;
    const totalRecargasMensuales = rawData.totalRecargasMensuales || 0;
    const totalMiselaneos = rawData.totalMiselaneos || 0;

    // ========== CÁLCULO CORRECTO: Valor Neto = Bruto ÷ 1.19 ==========
    const valorNetoTarifas = obtenerValorNetoSinIVA(recaudoTarifasBruto);
    const ivaTarifas = obtenerIVA(recaudoTarifasBruto);

    const valorNetoMensuales = obtenerValorNetoSinIVA(totalRecargasMensuales);
    const ivaMensuales = obtenerIVA(totalRecargasMensuales);

    const valorNetoMiscelaneos = obtenerValorNetoSinIVA(totalMiselaneos);
    const ivaMiscelaneos = obtenerIVA(totalMiselaneos);

    const valorNetoPerdidos = obtenerValorNetoSinIVA(perdidosTotalBruto);
    const ivaPerdidos = obtenerIVA(perdidosTotalBruto);

    // ========== TOTAL DE IVA (suma de todos los IVAs calculados) ==========
    const totalIVACalculado = ivaTarifas + ivaMensuales + ivaMiscelaneos + ivaPerdidos;

    // ========== VALORES NETOS (sin impuestos) ==========
    const recaudoTarifasNeto = valorNetoTarifas;
    const totalRecargasMensualesSinIva = valorNetoMensuales;
    const miselaneosSinIva = valorNetoMiscelaneos;
    const perdidosTotalSinIvaCalculado = valorNetoPerdidos;

    // ========== SUBTOTAL SIN IMPUESTOS ==========
    const subtotalSinImpuestos =
      recaudoTarifasNeto +
      totalRecargasMensualesSinIva +
      miselaneosSinIva +
      perdidosTotalSinIvaCalculado;

    // ========== BALANCE DE CAJA (total con impuestos incluidos) ==========
    const balanceCajaMovimientoDiario =
      recaudoTarifasBruto + totalRecargasMensuales + totalMiselaneos + perdidosTotalBruto;

    // ========== CANTIDADES ==========
    const cantidadTarifas = countOperationsEfe + countOperationsTar;
    const cantidadMensuales = recargasMensuales;
    const cantidadMiscelaneosVal = miselaneos;
    const cantidadImpuestos =
      cantidadTarifas + perdidosCantidad + cantidadMensuales + cantidadMiscelaneosVal;
    const totalCantidadRecaudos =
      cantidadTarifas + perdidosCantidad + cantidadMensuales + cantidadMiscelaneosVal;

    const retiros = 0;
    const transacciones = totalCantidadRecaudos;
    const cancelaciones = countCobrosCancelados;
    const inicioSistema = turn;

    // ========== CONTEO VEHICULAR ==========
    const vehiculosConTiquetes =
      (rawData.countCarros || 0) + (rawData.countMotos || 0) + (rawData.countBicis || 0);
    const tiquetesPerdidos = rawData.countTicketPerdidos || 0;
    const salidaEnGracia = 0;

    const facturasRaw = rawData.facturasEmiAnu || [];
    const facturaAnulada = facturasRaw.find((item) => item.status_descripcion === "ANULADA");
    const facturasAnuladas = facturaAnulada ? parseInt(facturaAnulada.total, 10) : 0;
    const facturasEmitidas = vehiculosConTiquetes - facturasAnuladas;
    const sinFacturaPorDescuento = rawData.SinFacturaSinDescuentos || 0;

    const conteoVehicularData = [
      {
        codigo: "T1",
        descripcion: "CARRO",
        cantidad: rawData.countCarros || 0,
        total: rawData.tarifaCarroEfe || 0,
      },
      {
        codigo: "T1",
        descripcion: "CARRO SIN COBRO",
        cantidad: rawData.countSinCobroCarro || 0,
        total: 0,
      },
      {
        codigo: "T2",
        descripcion: "MOTOS",
        cantidad: rawData.countMotos || 0,
        total: rawData.tarifaMotoEfe || 0,
      },
      {
        codigo: "T2",
        descripcion: "MOTOS SIN COBRO",
        cantidad: rawData.countSinCobroMoto || 0,
        total: 0,
      },
      {
        codigo: "T3",
        descripcion: "BICICLETAS",
        cantidad: rawData.countBicis || 0,
        total: rawData.tarifaBiciEfe || 0,
      },
      {
        codigo: "T3",
        descripcion: "BICI SIN COBROS",
        cantidad: rawData.countSinCobroBici || 0,
        total: 0,
      },
    ];

    const primerLogin = rawData.login?.init || null;
    const ultimoCierre = rawData.login?.end || null;

    return {
      raw: rawData,

      contadores: {
        transacciones: transacciones,
        cancelaciones: cancelaciones,
        inicioSistema: inicioSistema,
        aperturaBarreraEntrada: 0,
        aperturaBarreraSalida: 0,
        salidaContingencia: 0,
        tiquetesPurgados: 0,
      },

      // ========== TABLA RECAUDOS CORREGIDA ==========
      recaudosTable: {
        tarifas: {
          cantidad: cantidadTarifas,
          total: recaudoTarifasNeto, // Neto (sin IVA)
          bruto: recaudoTarifasBruto, // Bruto (con IVA incluido)
          impuestos: ivaTarifas, // IVA desglosado
        },
        perdidos: {
          cantidad: perdidosCantidad,
          total: perdidosTotalSinIvaCalculado, // Neto (sin IVA)
          totalBruto: perdidosTotalBruto, // Bruto (con IVA incluido)
          iva: ivaPerdidos, // IVA desglosado
        },
        contingencias: { cantidad: 0, total: 0 },
        mensuales: {
          cantidad: cantidadMensuales,
          total: totalRecargasMensualesSinIva, // Neto (sin IVA)
          bruto: totalRecargasMensuales, // Bruto (con IVA incluido)
          impuestos: ivaMensuales, // IVA desglosado
        },
        recargasPrepagos: { cantidad: 0, total: 0 },
        miscelaneos: {
          cantidad: cantidadMiscelaneosVal,
          total: miselaneosSinIva, // Neto (sin IVA)
          bruto: totalMiselaneos, // Bruto (con IVA incluido)
          impuestos: ivaMiscelaneos, // IVA desglosado
        },
        // ✅ IMPUESTOS: SUMA DE TODOS LOS IVAS CALCULADOS
        impuestos: {
          cantidad: cantidadImpuestos,
          total: totalIVACalculado, // Suma de ivaTarifas + ivaMensuales + ivaMiscelaneos + ivaPerdidos
        },
        // ✅ Subtotal sin impuestos
        subtotalSinImpuestos: subtotalSinImpuestos,
        // ✅ TOTAL RECAUDO = Balance de Caja (con impuestos incluidos)
        totalRecaudo: {
          cantidad: totalCantidadRecaudos,
          total: balanceCajaMovimientoDiario,
        },
      },

      conteoResumen: {
        vehiculosConTiquetes: vehiculosConTiquetes,
        tiquetesPerdidos: tiquetesPerdidos,
        salidaEnGracia: salidaEnGracia,
        facturasEmitidas: facturasEmitidas,
        facturasAnuladas: facturasAnuladas,
        sinFacturaPorDescuento: sinFacturaPorDescuento,
      },

      conteoVehicularTable: conteoVehicularData,

      totals: {
        efectivo: {
          total: totalOperationsEfe,
          count: countOperationsEfe,
          impuesto: totalOperationsEfeImp,
        },
        tarjeta: {
          total: totalOperationsTar,
          count: countOperationsTar,
          impuesto: totalOperationsTarImp,
        },
        formasPago: {
          efectivo: {
            total: rawData.totalFormaPagoEfe || 0,
            count: rawData.countFormaPagoEfe || 0,
          },
          tarjeta: {
            total: rawData.totalFormaPagoTar || 0,
            count: rawData.countFormaPagoTar || 0,
          },
        },
        totalOperations: rawData.operations || 0,
        totalFacturacion: recaudoTarifasBruto,
        totalFacturacionNeta: recaudoTarifasNeto,
        totalImpuestos: totalIVACalculado,
      },

      recaudos: {
        tarifas: recaudoTarifasBruto,
        tarifasBruto: recaudoTarifasBruto,
        tarifasNeto: recaudoTarifasNeto,
        tarifasImpuestos: ivaTarifas,
        mensuales: totalRecargasMensuales,
        miselaneos: totalMiselaneos,
        retiros: retiros,
        perdidos: perdidosTotalBruto,
        perdidosSinIva: perdidosTotalSinIvaCalculado,
        countPerdidos: perdidosCantidad,
        balanceCaja: balanceCajaMovimientoDiario,
      },

      facturacion: {
        fecha: rawData.facturacion?.fecha || null,
        primeraFactura: rawData.facturacion?.primera_factura || null,
        primeraFecha: rawData.facturacion?.primera_fecha || null,
        ultimaFactura: rawData.facturacion?.ultima_factura || null,
        ultimaFecha: rawData.facturacion?.ultima_fecha || null,
      },

      login: { primerLogin: primerLogin, ultimoCierre: ultimoCierre },

      facturacionDesc: {
        init: {
          fecha: rawData.facturacionDesc?.init?.fecha || null,
          prefijoFactura: rawData.facturacionDesc?.init?.prefijo_factura || null,
          numeroFactura: rawData.facturacionDesc?.init?.numero_factura || null,
          fechaHoraCompleta: rawData.facturacionDesc?.init?.fecha_hora_completa || null,
          statusDescripcion: rawData.facturacionDesc?.init?.status_descripcion || null,
          statusOriginal: rawData.facturacionDesc?.init?.status_original || null,
        },
        end: {
          fecha: rawData.facturacionDesc?.end?.fecha || null,
          prefijoFactura: rawData.facturacionDesc?.end?.prefijo_factura || null,
          numeroFactura: rawData.facturacionDesc?.end?.numero_factura || null,
          fechaHoraCompleta: rawData.facturacionDesc?.end?.fecha_hora_completa || null,
          statusDescripcion: rawData.facturacionDesc?.end?.status_descripcion || null,
          statusOriginal: rawData.facturacionDesc?.end?.status_original || null,
        },
      },

      estadoContable: {
        actual: rawData.estadoContableActual || 0,
        anterior: rawData.estadoContableAnterior || 0,
        diferencia: (rawData.estadoContableActual || 0) - (rawData.estadoContableAnterior || 0),
      },

      movimientos: {
        depositos: rawData.depositos || 0,
        totalDepositos: rawData.totalDepositos || 0,
        miselaneos: rawData.miselaneos || 0,
        miselaneosSinIva: miselaneosSinIva,
        totalMiselaneos: rawData.totalMiselaneos || 0,
        recargasMensuales: rawData.recargasMensuales || 0,
        totalRecargasMensualesSinIva: totalRecargasMensualesSinIva,
        totalRecargasMensuales: rawData.totalRecargasMensuales || 0,
        descuentosTarjeta: rawData.descuentosTarjeta || 0,
        descuentosEfectivo: rawData.descuentosEfectivo || 0,
        totalDescuentosTarjeta: rawData.totalDescuentosTarjeta || 0,
        descuentos: rawData.descuentos || 0,
        totalDescuentos: rawData.totalDescuentos || 0,
        ticketesPerdidos: rawData.ticketesPerdidos || 0,
        countTicketPerdidos: rawData.countTicketPerdidos || 0,
        ivaPerdidos: ivaPerdidos,
        cobrosCancelados: rawData.cobrosCancelados || 0,
        countCobrosCancelados: rawData.countCobrosCancelados || 0,
        descuentosComercio: rawData.descuentosComercio || [],
      },

      turn: rawData.turn || 0,
    };
  }, []);

  // ========== CONFIGURACIÓN DE FORMIK ==========

  const getInitialValues = useCallback(() => {
    const today = new Date().toISOString().split("T")[0];
    return {
      init: initialData?.init || today,
      end: initialData?.end || today,
      user_kme: initialData?.user_kme || "TODOS",
      box: initialData?.box || "TODAS",
    };
  }, [initialData]);

  const validationSchema = Yup.object().shape({
    init: Yup.date()
      .required("La fecha inicial es obligatoria")
      .typeError("Formato de fecha inicial inválido")
      .test("is-valid-format", "Formato de fecha inicial inválido. Use YYYY-MM-DD", (value) => {
        if (!value) return true;
        const date = new Date(value);
        return !isNaN(date.getTime());
      }),
    end: Yup.date()
      .required("La fecha final es obligatoria")
      .typeError("Formato de fecha final inválido")
      .test("is-valid-format", "Formato de fecha final inválido. Use YYYY-MM-DD", (value) => {
        if (!value) return true;
        const date = new Date(value);
        return !isNaN(date.getTime());
      })
      .test(
        "is-after-init",
        "La fecha final debe ser mayor o igual a la fecha inicial",
        function (value) {
          const { init } = this.parent;
          if (!init || !value) return true;
          const initDate = new Date(init);
          const endDate = new Date(value);
          initDate.setHours(0, 0, 0, 0);
          endDate.setHours(0, 0, 0, 0);
          return endDate >= initDate;
        }
      ),
    user_kme: Yup.string(),
    box: Yup.mixed(),
  });

  const handleGetReport = useCallback(
    async (values) => {
      try {
        const params = {
          init: values.init,
          end: values.end,
        };
        if (values.user_kme && values.user_kme !== "TODOS") params.user_kme = values.user_kme;
        if (values.box && values.box !== "TODAS" && values.box !== "")
          params.box = Number(values.box);

        const result = await getDailyReportService(params);
        if (!result?.success) throw new Error(result?.error || "Error al obtener el reporte");

        const processedData = processReportData(result.data?.raw || result.data);
        return { ...result, data: processedData };
      } catch (error) {
        throw error;
      }
    },
    [processReportData]
  );

  const formik = useFormik({
    initialValues: getInitialValues(),
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        setSubmitError(null);
        setSubmitSuccess(false);
        setLoading(true);
        setLastErrorType(null);
        setReportData(null);

        const reportResult = await handleGetReport(values);
        setReportData(reportResult.data);
        setSubmitSuccess(true);

        const resultData = {
          ...values,
          reportData: reportResult.data,
          success: true,
          timestamp: new Date().toISOString(),
        };
        if (onSubmit && typeof onSubmit === "function") await onSubmit(resultData);
        return resultData;
      } catch (error) {
        const errorMessage = extractErrorMessage(error);
        const errorClassification = classifyError({ message: errorMessage });
        setSubmitError(errorMessage);
        setLastErrorType(errorClassification.type);
        setReportData(null);

        const errorData = {
          success: false,
          error: errorMessage,
          errorType: errorClassification.type,
          values,
          timestamp: new Date().toISOString(),
        };
        if (onSubmit && typeof onSubmit === "function") await onSubmit(errorData);
        return errorData;
      } finally {
        setLoading(false);
        setSubmitting(false);
      }
    },
  });

  const executeSubmit = useCallback(async () => {
    if (formik.isSubmitting || loading)
      return { success: false, error: "Operación ya en progreso" };
    return formik.submitForm();
  }, [formik, loading]);

  const getSelectedUserName = useCallback(() => {
    const selectedValue = formik.values.user_kme;
    if (selectedValue && users.length > 0) {
      const user = users.find((u) => u.value === selectedValue || u.id === selectedValue);
      return user?.name || selectedValue;
    }
    return selectedValue || "TODOS";
  }, [formik.values.user_kme, users]);

  const getSelectedBoxName = useCallback(() => {
    const selectedValue = formik.values.box;
    if (selectedValue && boxes.length > 0) {
      const box = boxes.find((b) => b.value === selectedValue || b.id === selectedValue);
      return box?.name || selectedValue;
    }
    return selectedValue || "TODAS";
  }, [formik.values.box, boxes]);

  const reloadUsers = useCallback(async () => {
    if (loadingUsers) return { success: false, error: "Ya cargando usuarios" };
    hasLoadedDataRef.current = false;
    setUsers([{ id: "TODOS", name: "TODOS", label: "TODOS", value: "TODOS" }]);
    setErrorUsers(null);
    try {
      await loadUsersAndBoxes();
      return { success: true };
    } catch (error) {
      return { success: false, error: extractErrorMessage(error) };
    }
  }, [loadUsersAndBoxes, loadingUsers, extractErrorMessage]);

  const reloadBoxes = useCallback(async () => {
    if (loadingBoxes) return { success: false, error: "Ya cargando cajas" };
    hasLoadedDataRef.current = false;
    setBoxes([{ id: "TODAS", name: "TODAS", label: "TODAS", value: "TODAS" }]);
    setErrorBoxes(null);
    try {
      await loadUsersAndBoxes();
      return { success: true };
    } catch (error) {
      return { success: false, error: extractErrorMessage(error) };
    }
  }, [loadUsersAndBoxes, loadingBoxes, extractErrorMessage]);

  const resetFormState = useCallback(() => {
    if (!isMounted.current) return;
    const today = new Date().toISOString().split("T")[0];
    formik.resetForm({ values: { init: today, end: today, user_kme: "TODOS", box: "TODAS" } });
    setSubmitError(null);
    setSubmitSuccess(false);
    setReportData(null);
    setLastErrorType(null);
  }, [formik]);

  const formState = useMemo(
    () => ({
      values: formik.values,
      errors: formik.errors,
      touched: formik.touched,
      isSubmitting: formik.isSubmitting,
      loading,
      isValid: formik.isValid,
      dirty: formik.dirty,
      handleChange: formik.handleChange,
      handleBlur: formik.handleBlur,
      handleSubmit: formik.handleSubmit,
      setFieldValue: formik.setFieldValue,
      setFieldTouched: formik.setFieldTouched,
      validateForm: formik.validateForm,
      executeSubmit,
      submitError,
      submitSuccess,
      setSubmitError,
      setSubmitSuccess,
      reportData,
      lastErrorType,
      users,
      loadingUsers,
      errorUsers,
      reloadUsers,
      getSelectedUserName,
      boxes,
      loadingBoxes,
      errorBoxes,
      reloadBoxes,
      getSelectedBoxName,
      resetForm: resetFormState,
      hasUsers: users.length > 1,
      hasBoxes: boxes.length > 1,
      hasError: !!submitError,
      hasSuccess: submitSuccess,
    }),
    [
      formik.values,
      formik.errors,
      formik.touched,
      formik.isSubmitting,
      formik.isValid,
      formik.dirty,
      formik.handleChange,
      formik.handleBlur,
      formik.handleSubmit,
      formik.setFieldValue,
      formik.setFieldTouched,
      formik.validateForm,
      loading,
      executeSubmit,
      submitError,
      submitSuccess,
      reportData,
      lastErrorType,
      users,
      loadingUsers,
      errorUsers,
      reloadUsers,
      getSelectedUserName,
      boxes,
      loadingBoxes,
      errorBoxes,
      reloadBoxes,
      getSelectedBoxName,
      resetFormState,
    ]
  );

  return formState;
};

export default useFormInforme;
